/**
 * Satjeevan — Global Scroll Reveal Animation System (sj-scroll-reveal.js)
 *
 * Handles:
 *   1. Google Fonts async loading (Fraunces + Inter)
 *   2. Scroll-triggered reveal animations via IntersectionObserver
 *   3. Progressive heading animations (20-30% threshold)
 *   4. Independent row-by-row animations for grids
 *   5. Shopify Theme Editor live-reload compatibility
 *
 * No dependencies — vanilla JS only.
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Tracking — prevent duplicate initialisation
     ----------------------------------------------------------------------- */
  var SJ_INIT_ATTR = 'data-sj-reveal-init';

  /* -----------------------------------------------------------------------
     1. Load Google Fonts if not already present
     ----------------------------------------------------------------------- */
  function ensureGoogleFonts() {
    if (document.querySelector('[data-sj-fonts]')) return;

    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://fonts.googleapis.com';
    document.head.appendChild(link);

    var link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,ital,wght@9..144,0,400;9..144,0,500;9..144,0,600;9..144,0,700;9..144,1,400;9..144,1,500;9..144,1,600;9..144,1,700&family=Inter:wght@400;500;600;700&display=swap';
    link2.setAttribute('data-sj-fonts', '1');
    document.head.appendChild(link2);
  }

  /* -----------------------------------------------------------------------
     Helpers
     ----------------------------------------------------------------------- */

  /**
   * Check if an element is currently within the viewport.
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  /**
   * Group an array of HTMLElements into rows based on their offsetTop value.
   * Returns an object keyed by offsetTop, each value an array of HTMLElements.
   * Skips elements already revealed.
   * @param {HTMLElement[]} children
   * @returns {Object.<number, HTMLElement[]>}
   */
  function buildRowGroups(children) {
    /** @type {Object.<number, HTMLElement[]>} */
    var groups = {};
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (!child || child.classList.contains('sj-in-view')) continue;
      var top = child.offsetTop;
      if (!groups[top]) {
        groups[top] = [];
      }
      var group = groups[top];
      if (group) {
        group.push(child);
      }
    }
    return groups;
  }

  /**
   * Reveal all elements in a row with staggered delays.
   * @param {HTMLElement[]} row
   * @param {IntersectionObserver|null} observer  — if provided, unobserve each
   */
  function revealRow(row, observer) {
    for (var i = 0; i < row.length; i++) {
      var el = row[i];
      if (!el) continue;
      el.style.setProperty('--sj-stagger-delay', (i * 80) + 'ms');
      el.classList.add('sj-in-view');
      if (observer) {
        observer.unobserve(el);
      }
    }
  }

  /* -----------------------------------------------------------------------
     2. Core initialisation
     ----------------------------------------------------------------------- */
  function initScrollReveal() {
    ensureGoogleFonts();

    /* ----- 2a. Standard single-element reveals ----- */
    var revealElements = document.querySelectorAll('[data-sj-reveal]:not([' + SJ_INIT_ATTR + '])');

    if (revealElements.length) {
      var revealObserver = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry && entry.isIntersecting) {
            entry.target.classList.add('sj-in-view');
            revealObserver.unobserve(entry.target);
          }
        }
      }, { threshold: 0.1 });

      for (var r = 0; r < revealElements.length; r++) {
        var el = /** @type {HTMLElement} */ (revealElements[r]);
        el.setAttribute(SJ_INIT_ATTR, '');
        if (isInViewport(el)) {
          el.classList.add('sj-in-view');
        } else {
          revealObserver.observe(el);
        }
      }
    }

    /* ----- 2b. Heading container reveals (25 % threshold) ----- */
    var headingContainers = document.querySelectorAll('[data-sj-reveal-heading]:not([' + SJ_INIT_ATTR + '])');

    if (headingContainers.length) {
      var headingObserver = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry && entry.isIntersecting) {
            var revealChildren = entry.target.querySelectorAll('.sj-reveal');
            for (var c = 0; c < revealChildren.length; c++) {
              var child = revealChildren[c];
              if (child) {
                child.classList.add('sj-in-view');
              }
            }
            headingObserver.unobserve(entry.target);
          }
        }
      }, { threshold: 0.25 });

      for (var h = 0; h < headingContainers.length; h++) {
        var hEl = /** @type {HTMLElement} */ (headingContainers[h]);
        hEl.setAttribute(SJ_INIT_ATTR, '');
        if (isInViewport(hEl)) {
          var hChildren = hEl.querySelectorAll('.sj-reveal');
          for (var hc = 0; hc < hChildren.length; hc++) {
            var hChild = hChildren[hc];
            if (hChild) {
              hChild.classList.add('sj-in-view');
            }
          }
        } else {
          headingObserver.observe(hEl);
        }
      }
    }

    /* ----- 2c. Grid row-by-row reveals ----- */
    var grids = document.querySelectorAll('[data-sj-reveal-grid]:not([' + SJ_INIT_ATTR + '])');

    for (var g = 0; g < grids.length; g++) {
      (function (/** @type {HTMLElement} */ grid) {
        grid.setAttribute(SJ_INIT_ATTR, '');

        /** @type {HTMLElement[]} */
        var children = Array.prototype.slice.call(grid.children);

        // Tag each child with reveal classes
        for (var ci = 0; ci < children.length; ci++) {
          var child = children[ci];
          if (child) {
            child.classList.add('sj-reveal', 'sj-reveal-up', 'sj-reveal-stagger');
          }
        }

        // Wait one frame so the browser computes layout with the new classes
        requestAnimationFrame(function () {
          var rowGroups = buildRowGroups(children);

          var gridObserver = new IntersectionObserver(function (entries) {
            for (var ei = 0; ei < entries.length; ei++) {
              var entry = entries[ei];
              if (!entry || !entry.isIntersecting) continue;

              var target = /** @type {HTMLElement} */ (entry.target);
              var top = target.offsetTop;
              var row = rowGroups[top];

              if (row) {
                revealRow(row, gridObserver);
                delete rowGroups[top];
              } else {
                // Safety: if offsetTop shifted, unobserve the orphan
                target.classList.add('sj-in-view');
                gridObserver.unobserve(target);
              }
            }
          }, { threshold: 0.6 });

          // Above-the-fold rows: reveal immediately with stagger
          /** @type {Object.<number, boolean>} */
          var aboveFoldTops = {};
          for (var ai = 0; ai < children.length; ai++) {
            var childAi = children[ai];
            if (childAi && isInViewport(childAi) && !childAi.classList.contains('sj-in-view')) {
              aboveFoldTops[childAi.offsetTop] = true;
            }
          }

          var topKeys = Object.keys(aboveFoldTops);
          for (var tk = 0; tk < topKeys.length; tk++) {
            var key = Number(topKeys[tk]);
            var group = rowGroups[key];
            if (group) {
              revealRow(group, null);
              delete rowGroups[key];
            }
          }

          // Remaining off-screen children: observe
          for (var oi = 0; oi < children.length; oi++) {
            var childEl = children[oi];
            if (childEl && !childEl.classList.contains('sj-in-view')) {
              gridObserver.observe(childEl);
            }
          }

          // Recalculate row groups on resize (debounced)
          /** @type {number|undefined} */
          var resizeTimer;
          window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(function () {
              rowGroups = buildRowGroups(children);
            }, 200);
          });
        });
      })(/** @type {HTMLElement} */ (grids[g]));
    }
  }

  /* -----------------------------------------------------------------------
     3. Bootstrap
     ----------------------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollReveal);
  } else {
    initScrollReveal();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
  });

}());
